interface Constructor<T> {
    new(...args: any[]): T;
}

import { counter } from "@/utils/counters";
import { handleComponentError } from "@/utils/svelte/errors";
import { SvelteComponent } from "svelte";
import { CustomElement } from "./components";


const findAgainId = "tm-cs--instance";
let currentId = counter();
const instanceMap: Map<string, () => void> = new Map();


export function defineSvelteComponent(cb: () => Promise<{default: Constructor<SvelteComponent>}>): () => Promise<{default: CustomElement}> {
    function readAttributes(element: HTMLElement): Map<string, string> {
        const current = new Map();
        for (let attribute of element.getAttributeNames()) {
            current.set(attribute, element.getAttribute(attribute)!);
        }
        current.delete(findAgainId);

        return current;
    }

    function diffAttributes(before: Map<string, string>, after: Map<string, string>): Map<string, string|null> {
        const result = new Map();
        for (let name of after.keys()) {
            if (!before.has(name) || before.get(name) !== after.get(name)) {
                result.set(name, after.get(name));
            }
        }

        for (let name of before.keys()) {
            if (!after.has(name)) {
                result.set(name, null);
            }
        }

        return result;
    }

    function asObject(map: Map<string, string|null>): Record<string, string|null> {
        const result: Record<string, string|null> = {}
        for (let [key, value] of map.entries()) {
            result[key] = value;
        }
        return result;
    }

    return async () => {
        const impl = (await cb()).default;

        return {default: {
            apply(element: HTMLUnknownElement) {
                let otherAttr;
                if (element.hasAttribute(findAgainId) && instanceMap.has(otherAttr = element.getAttribute(findAgainId)!)) {
                    console.warn("Out of band rebinding of", element, otherAttr);
                    instanceMap.get(otherAttr)!();
                    instanceMap.delete(otherAttr);
                }

                const selfId = ""+currentId();
                element.setAttribute(findAgainId, selfId);

                let attrs = readAttributes(element);
                const component = new impl({
                    target: element,
                    props: {
                        ...asObject(attrs),
                        _target: element
                    }
                });
                handleComponentError(component);

                const observer = new MutationObserver((events) => {
                    for (let _ of events) {
                        const newAttrs = readAttributes(element);

                        const changes = diffAttributes(attrs, newAttrs);
                        if (changes.size == 0) continue;
                        attrs = newAttrs;

                        component.$set(asObject(changes));
                    }
                });
                observer.observe(element, {childList: false, attributes: true});

                let destroyed = false;
                const destroy = () => {
                    if (destroyed) return;
                    destroyed = true;

                    observer.disconnect();
                    component.$destroy();

                    instanceMap.delete(selfId);
                };
                instanceMap.set(selfId, destroy);
                return destroy;
            }
        }};
    }
}
